/* Dialog that asks for order details (thread length, machine type, …) before a
   product line is first added to the basket. Rendered by the
   quick-order-questions snippet; QuickOrderList calls open() and receives the
   answers as a line item properties object, or null when cancelled. */
if (!customElements.get('quick-order-questions')) {
  customElements.define(
    'quick-order-questions',
    class QuickOrderQuestions extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.querySelectorAll('[data-action="cancel"]').forEach((button) =>
          button.addEventListener('click', () => this.close(null))
        );
        // Clicking the backdrop (the element itself) cancels
        this.addEventListener('click', (event) => {
          if (event.target === this) this.close(null);
        });
        this.addEventListener('keyup', (event) => {
          if (event.code?.toUpperCase() === 'ESCAPE') this.close(null);
        });
      }

      connectedCallback() {
        // The quick order list can sit inside a transformed container (collection
        // quick-add modal) which would break this fixed overlay, so keep a single
        // copy of the dialog directly under <body>.
        if (this.parentElement === document.body) return;
        document.querySelectorAll(`quick-order-questions[id="${this.id}"]`).forEach((element) => {
          if (element !== this) element.remove();
        });
        document.body.appendChild(this);
      }

      get hasQuestions() {
        return this.form.querySelector('[data-property-name]') !== null;
      }

      get isOpen() {
        return !this.hasAttribute('hidden');
      }

      /**
       * @param {Object} options
       * @param {string} options.title Variant / product title shown in the dialog
       * @param {string} [options.sku]
       * @param {number} options.quantity
       * @param {Object} [options.answers] Previous answers used to prefill the fields
       * @param {HTMLElement} [options.opener] Element to refocus when the dialog closes
       * @returns {Promise<Object|null>} properties, or null if cancelled
       */
      open({ title, sku, quantity, answers, opener } = {}) {
        this.close(null);
        this.opener = opener;

        const template = this.dataset.addingTemplate || '[quantity] × [title]';
        this.querySelector('[data-question-item-title]').textContent = template
          .replace('[quantity]', quantity)
          .replace('[title]', title || '');
        const skuElement = this.querySelector('[data-question-item-sku]');
        skuElement.textContent = sku || '';
        skuElement.hidden = !sku;

        this.form.reset();
        Object.entries(answers || {}).forEach(([name, value]) => {
          const field = this.form.querySelector(`[data-property-name="${CSS.escape(name)}"]`);
          if (field) field.value = value;
        });

        this.removeAttribute('hidden');
        document.body.classList.add('overflow-hidden');
        trapFocus(this, this.form.querySelector('[data-property-name]') || this.querySelector('[role="dialog"]'));

        return new Promise((resolve) => {
          this.resolve = resolve;
        });
      }

      close(result) {
        if (this.isOpen) {
          this.setAttribute('hidden', '');
          document.body.classList.remove('overflow-hidden');
          removeTrapFocus(this.opener);
        }

        const resolve = this.resolve;
        this.resolve = null;
        this.opener = null;
        resolve?.(result);
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.form.reportValidity()) return;

        const properties = {};
        this.form.querySelectorAll('[data-property-name]').forEach((field) => {
          const value = field.value.trim();
          if (value) properties[field.dataset.propertyName] = value;
        });

        this.close(properties);
      }
    }
  );
}

if (!customElements.get('quick-order-list')) {
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends BulkAdd {
      cartUpdateUnsubscriber = undefined;
      hasPendingQuantityUpdate = false;
      constructor() {
        super();
        this.isListInsideModal = this.closest('bulk-modal');

        this.stickyHeaderElement = document.querySelector('sticky-header');
        if (this.stickyHeaderElement) {
          this.stickyHeader = {
            height: this.stickyHeaderElement.offsetHeight,
            type: `${this.stickyHeaderElement.getAttribute('data-sticky-type')}`,
          };
        }

        this.querySelector('form').addEventListener('submit', (event) => event.preventDefault());
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
          // skip if cart event was triggered by this section
          if (event.source === this.id) return;

          this.toggleTableLoading(true);
          await this.refresh();
          this.toggleTableLoading(false);
        });

        this.initEventListeners();
        this.initColumnsView();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
      }

      initEventListeners() {
        this.querySelectorAll('.pagination__item').forEach((link) => {
          link.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();

            const url = new URL(event.currentTarget.href);

            this.toggleTableLoading(true);
            await this.refresh(url.searchParams.get('page') || '1');
            this.scrollTop();
            this.toggleTableLoading(false);
          });
        });

        this.querySelector('.quick-order-list__contents').addEventListener(
          'keyup',
          this.handleScrollIntoView.bind(this)
        );

        this.quickOrderListTable.addEventListener('keydown', this.handleSwitchVariantOnEnter.bind(this));

        this.initVariantEventListeners();
        this.initFilterEventListeners();
      }

      initFilterEventListeners() {
        this.selectedFilters = this.selectedFilters || {};

        this.querySelectorAll('.quick-order-list__filter-select').forEach((select) => {
          const position = select.dataset.optionPosition;

          // Restore the selection after the section HTML is re-rendered
          if (this.selectedFilters[position]) {
            const hasValue = Array.from(select.options).some((option) => option.value === this.selectedFilters[position]);
            if (hasValue) {
              select.value = this.selectedFilters[position];
            } else {
              delete this.selectedFilters[position];
            }
          }

          select.addEventListener('change', () => {
            if (select.value) {
              this.selectedFilters[position] = select.value;
            } else {
              delete this.selectedFilters[position];
            }
            this.applyVariantFilters(true);
          });
        });

        this.querySelector('.quick-order-list__filters-clear')?.addEventListener('click', () => {
          this.selectedFilters = {};
          this.querySelectorAll('.quick-order-list__filter-select').forEach((select) => (select.value = ''));
          this.applyVariantFilters(true);
        });

        this.applyVariantFilters();
      }

      get totalPages() {
        return parseInt(this.querySelector('#main-variant-items')?.dataset.totalPages ?? '1', 10);
      }

      applyVariantFilters(showIndicator = false) {
        const activeFilters = Object.entries(this.selectedFilters || {});

        this.syncColumnsViewUI();
        if (showIndicator) this.showFilteringIndicator();

        // With filters or a sort active, load every page of variants first so
        // they span the whole product rather than just the current page.
        if ((activeFilters.length > 0 || this.sortState) && this.totalPages > 1 && !this.allVariantsLoaded) {
          this.loadAllVariants().then(() => {
            this.filterRows();
            this.sortRows();
            this.hideFilteringIndicator();
          });
          return;
        }

        // Once all filters are cleared (and no sort), restore the regular paginated view.
        if (activeFilters.length === 0 && !this.sortState && this.allVariantsLoaded) {
          this.toggleTableLoading(true);
          this.refresh().then(() => {
            this.toggleTableLoading(false);
            this.hideFilteringIndicator();
          });
          return;
        }

        this.filterRows();
        this.sortRows();
        this.hideFilteringIndicator();
      }

      showFilteringIndicator(mode = 'filtering') {
        const indicator = this.querySelector('.quick-order-list__filtering');
        if (!indicator) return;
        const text = indicator.querySelector('span');
        const modeText = mode === 'sorting' ? indicator.dataset.sortingText : indicator.dataset.filteringText;
        if (text && modeText) text.textContent = modeText;
        clearTimeout(this.filteringIndicatorTimeout);
        this.filteringIndicatorShownAt = Date.now();
        indicator.classList.remove('hidden');
      }

      hideFilteringIndicator() {
        const indicator = this.querySelector('.quick-order-list__filtering');
        if (!indicator || indicator.classList.contains('hidden')) return;
        // Keep the pop-up on screen briefly even when filtering is instant
        const minVisibleMs = 600;
        const remaining = Math.max(0, minVisibleMs - (Date.now() - (this.filteringIndicatorShownAt || 0)));
        clearTimeout(this.filteringIndicatorTimeout);
        this.filteringIndicatorTimeout = setTimeout(() => indicator.classList.add('hidden'), remaining);
      }

      /* ---- Columns view: toggle, per-column filters and sorting ----
         Listeners are delegated to this element (which survives the frequent
         innerHTML swaps); state is re-applied via applyVariantFilters, which
         already runs after every re-render. */

      initColumnsView() {
        if (this.columnsViewInitialized) return;
        this.columnsViewInitialized = true;

        this.viewPreference = 'list';
        try {
          this.viewPreference = window.localStorage.getItem('ims:quick-order-view') || 'list';
        } catch {
          /* storage unavailable — default view stands */
        }

        this.addEventListener('click', (event) => {
          const viewButton = event.target.closest('.quick-order-list__view-button');
          if (viewButton) {
            this.setView(viewButton.dataset.view);
            return;
          }

          const sortButton = event.target.closest('.quick-order-list__sort-button');
          if (sortButton) this.onSortClicked(sortButton.dataset.sortKey);
        });

        this.addEventListener('change', (event) => {
          const select = event.target.closest('.quick-order-list__column-filter');
          if (!select) return;

          this.selectedFilters = this.selectedFilters || {};
          if (select.value) {
            this.selectedFilters[select.dataset.optionPosition] = select.value;
          } else {
            delete this.selectedFilters[select.dataset.optionPosition];
          }
          this.applyVariantFilters(true);
        });

        this.syncColumnsViewUI();
      }

      setView(view) {
        this.viewPreference = view;
        try {
          window.localStorage.setItem('ims:quick-order-view', view);
        } catch {
          /* non-persistent, but the toggle still works for this page view */
        }
        this.syncColumnsViewUI();
      }

      syncColumnsViewUI() {
        // Products without options render no columns header, so never apply the class there
        const supportsColumns = !!this.querySelector('.quick-order-list__columns-head');
        this.classList.toggle('quick-order-list--columns', supportsColumns && this.viewPreference === 'columns');

        this.querySelectorAll('.quick-order-list__view-button').forEach((button) => {
          const active = button.dataset.view === (this.viewPreference || 'list');
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active);
        });

        // Keep both filter UIs (top box and column headers) showing the same state
        this.querySelectorAll('.quick-order-list__column-filter, .quick-order-list__filter-select').forEach(
          (select) => {
            select.value = this.selectedFilters?.[select.dataset.optionPosition] || '';
            select.classList.toggle('quick-order-list__filter-select--active', select.value !== '');
          }
        );

        this.updateSortIndicators();
      }

      onSortClicked(key) {
        if (this.sortState?.key === key) {
          this.sortState.dir = -this.sortState.dir;
        } else {
          this.sortState = { key, dir: 1 };
        }
        this.updateSortIndicators();

        if (this.totalPages > 1 && !this.allVariantsLoaded) {
          this.showFilteringIndicator('sorting');
          this.loadAllVariants().then(() => {
            this.filterRows();
            this.sortRows();
            this.hideFilteringIndicator();
          });
          return;
        }

        this.sortRows();
      }

      sortRows() {
        if (!this.sortState) return;
        const tbody = this.quickOrderListTable?.querySelector('tbody');
        if (!tbody) return;

        const { key, dir } = this.sortState;
        const valueOf = (row) => (key === 'sku' ? row.dataset.sku : row.dataset[key]) || '';

        const rows = Array.from(tbody.querySelectorAll('tr.variant-item'));
        rows.sort((a, b) => dir * valueOf(a).localeCompare(valueOf(b), undefined, { numeric: true, sensitivity: 'base' }));

        const anchor = tbody.querySelector('.quick-order-list__no-results');
        rows.forEach((row) => {
          // Each variant row is followed by its hidden desktop error row; keep them paired
          const errorRow = row.nextElementSibling?.classList.contains('desktop-row-error')
            ? row.nextElementSibling
            : null;
          tbody.insertBefore(row, anchor);
          if (errorRow) tbody.insertBefore(errorRow, anchor);
        });

        // Keep Enter/Tab navigation aligned with the new visual order
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]')).filter(
          (input) => !input.closest('tr')?.classList.contains('variant-item--filtered-out')
        );
      }

      updateSortIndicators() {
        this.querySelectorAll('.quick-order-list__sort-button').forEach((button) => {
          const active = this.sortState?.key === button.dataset.sortKey;
          button.classList.toggle('is-sorted', active);
          button.classList.toggle('is-sorted-desc', active && this.sortState.dir === -1);
          button
            .closest('th')
            ?.setAttribute('aria-sort', active ? (this.sortState.dir === 1 ? 'ascending' : 'descending') : 'none');
        });
      }

      showCartStatus(items) {
        const indicator = this.querySelector('.quick-order-list__status');
        if (!indicator) return;

        const entries = Object.entries(items);
        const allRemovals = entries.every(([, quantity]) => quantity === 0);
        const allAdditions = entries.every(
          ([id, quantity]) => quantity > 0 && this.getCartQuantityForLine(id) === 0
        );
        const textKey = allRemovals ? 'textRemoving' : allAdditions ? 'textAdding' : 'textUpdating';

        indicator.querySelector('[data-status-text]').textContent = indicator.dataset[textKey] || '';
        clearTimeout(this.cartStatusTimeout);
        this.cartStatusShownAt = Date.now();
        indicator.classList.remove('hidden');
      }

      hideCartStatus() {
        const indicator = this.querySelector('.quick-order-list__status');
        if (!indicator || indicator.classList.contains('hidden')) return;
        // Keep the pop-up on screen briefly even when the request is instant
        const minVisibleMs = 600;
        const remaining = Math.max(0, minVisibleMs - (Date.now() - (this.cartStatusShownAt || 0)));
        clearTimeout(this.cartStatusTimeout);
        this.cartStatusTimeout = setTimeout(() => indicator.classList.add('hidden'), remaining);
      }

      async loadAllVariants() {
        if (this.loadAllVariantsPromise) return this.loadAllVariantsPromise;

        this.loadAllVariantsPromise = (async () => {
          this.toggleTableLoading(true);

          try {
            const url = this.dataset.url || window.location.pathname;
            const pageNumbers = Array.from({ length: this.totalPages }, (_, index) => index + 1);

            const pages = await Promise.all(
              pageNumbers.map((page) =>
                fetch(`${url}?section_id=${this.dataset.section}&page=${page}`)
                  .then((response) => response.text())
                  .then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'))
              )
            );

            const tbody = this.quickOrderListTable.querySelector('tbody');
            const noResultsRow = tbody.querySelector('.quick-order-list__no-results');
            tbody.innerHTML = '';

            pages.forEach((html) => {
              html
                .querySelectorAll(`#${this.id} .quick-order-list__table tbody > tr:not(.quick-order-list__no-results)`)
                .forEach((row) => tbody.appendChild(row));

              // Volume pricing templates are foster-parented outside the table
              // when the response is parsed; carry over any we don't have yet.
              html.querySelectorAll(`#${this.id} volume-pricing[id]`).forEach((volumePricing) => {
                if (!this.querySelector(`#${CSS.escape(volumePricing.id)}`)) {
                  this.querySelector('#main-variant-items')?.appendChild(volumePricing);
                }
              });
            });

            if (noResultsRow) tbody.appendChild(noResultsRow);

            this.querySelector('.js-paginate')?.classList.add('hidden');
            this.allVariantsLoaded = true;
            this.initVariantEventListeners();
          } catch (e) {
            console.error(e);
          } finally {
            this.toggleTableLoading(false);
            this.loadAllVariantsPromise = null;
          }
        })();

        return this.loadAllVariantsPromise;
      }

      filterRows() {
        const table = this.quickOrderListTable;
        if (!table) return;

        const activeFilters = Object.entries(this.selectedFilters || {});
        let visibleCount = 0;
        let totalCount = 0;

        table.querySelectorAll('tbody tr.variant-item').forEach((row) => {
          totalCount++;
          const matches = activeFilters.every(([position, value]) => row.dataset[`option${position}`] === value);

          row.classList.toggle('variant-item--filtered-out', !matches);

          // Each variant row is followed by a hidden desktop error row that must stay in sync
          const errorRow = row.nextElementSibling;
          if (errorRow?.classList.contains('desktop-row-error')) {
            errorRow.classList.toggle('variant-item--filtered-out', !matches);
          }

          if (matches) visibleCount++;
        });

        table.querySelector('.quick-order-list__no-results')?.classList.toggle('hidden', visibleCount > 0);

        this.updateFilterStatus(visibleCount, totalCount);

        // Keep Enter/Tab navigation within visible rows only
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]')).filter(
          (input) => !input.closest('tr')?.classList.contains('variant-item--filtered-out')
        );
      }

      updateFilterStatus(visibleCount, totalCount) {
        const filtersContainer = this.querySelector('.quick-order-list__filters');
        if (!filtersContainer) return;

        const selects = Array.from(this.querySelectorAll('.quick-order-list__filter-select'));
        selects.forEach((select) =>
          select.classList.toggle('quick-order-list__filter-select--active', select.value !== '')
        );

        const appliedParts = selects
          .filter((select) => select.value)
          .map((select) => `${select.dataset.optionName}: ${select.value}`);
        const hasFilters = appliedParts.length > 0;

        filtersContainer.querySelector('.quick-order-list__filters-clear')?.classList.toggle('hidden', !hasFilters);

        const statusElement = filtersContainer.querySelector('.quick-order-list__filters-status');
        if (!statusElement) return;

        statusElement.classList.toggle('hidden', !hasFilters);
        statusElement.textContent = hasFilters
          ? (filtersContainer.dataset.appliedTemplate || '[filters]')
              .replace('[filters]', appliedParts.join(', '))
              .replace('[count]', visibleCount)
              .replace('[total]', totalCount)
          : '';
      }

      initVariantEventListeners() {
        this.allInputsArray = Array.from(this.querySelectorAll('input[type="number"]'));

        this.querySelectorAll('quantity-input').forEach((qty) => {
          const debouncedOnChange = debounce(this.onChange.bind(this), BulkAdd.ASYNC_REQUEST_DELAY, true);
          qty.addEventListener('change', (event) => {
            this.hasPendingQuantityUpdate = true;
            debouncedOnChange(event);
          });
        });

        this.querySelectorAll('.quick-order-list-remove-button').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleLoading(true);
            this.startQueue(button.dataset.index, 0);
          });
        });
      }

      get currentPage() {
        return this.querySelector('.pagination-wrapper')?.dataset?.page ?? '1';
      }

      get cartVariantsForProduct() {
        return JSON.parse(this.querySelector('[data-cart-contents]')?.innerHTML || '[]');
      }

      getCartQuantityForLine(id) {
        const cartQuantity = super.getCartQuantityForLine(id);
        if (cartQuantity > 0) return cartQuantity;

        return this.cartVariantsForProduct.includes(parseInt(id, 10)) ? 1 : 0;
      }

      onChange(event) {
        const inputValue = parseInt(event.target.value);
        this.cleanErrorMessageOnType(event);
        if (inputValue == 0) {
          event.target.setAttribute('value', inputValue);
          this.startQueue(event.target.dataset.index, inputValue);
        } else {
          this.validateQuantity(event);
        }
      }

      /* ---- Order questions ----
         Some products need extra details from the customer (thread length,
         machine type, …) — see Theme settings > Order questions. The first time
         a variant goes into the basket we ask for them and add the line via
         /cart/add.js with the answers as line item properties. Later quantity
         changes go through /cart/update.js as usual, addressed by line key. */

      get questionsDialog() {
        return document.getElementById(`QuickOrderQuestions-${this.id}`);
      }

      needsOrderDetails(id, quantity) {
        return quantity > 0 && this.getCartQuantityForLine(id) === 0 && !!this.questionsDialog?.hasQuestions;
      }

      startQueue(id, quantity) {
        if (this.needsOrderDetails(id, quantity)) {
          this.collectOrderDetails(id, quantity);
          return;
        }
        super.startQueue(id, quantity);
      }

      lineKeyForVariant(id) {
        return this.querySelector(`#Variant-${id}`)?.dataset.lineKey || null;
      }

      collectOrderDetails(id, quantity) {
        const row = this.querySelector(`#Variant-${id}`);
        const input = this.querySelector(`#Quantity-${id}`);

        this.questionsDialog
          .open({
            title: row?.querySelector('.variant-item__name')?.textContent.trim(),
            sku: row?.dataset.sku,
            quantity,
            answers: this.lastOrderDetails,
            opener: input,
          })
          .then((properties) => {
            if (!properties) {
              // Cancelled: nothing was added, so put the stepper back to what's in the basket
              const cartQuantity = input?.dataset.cartQuantity || '0';
              if (input) {
                input.value = cartQuantity;
                input.setAttribute('value', cartQuantity);
                input.closest('quantity-input')?.validateQtyRules();
              }
              this.hasPendingQuantityUpdate = false;
              return;
            }

            // Remember the answers so the next line for this product is prefilled
            this.lastOrderDetails = properties;
            this.addWithProperties(id, quantity, properties);
          });
      }

      addWithProperties(id, quantity, properties) {
        this.hasPendingQuantityUpdate = false;
        this.toggleLoading(true);

        const items = { [id]: quantity };
        this.showCartStatus(items);
        const url = this.dataset.url || window.location.pathname;
        const linesUpdate = this.startCartLinesUpdate(items);

        const body = JSON.stringify({
          items: [{ id: parseInt(id, 10), quantity, properties }],
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: `${url}?page=${this.currentPage}`,
        });

        this.updateMessage();
        this.setErrorMessage();

        fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              throw Object.assign(new Error(parsedState.description || parsedState.message), { code: 'INVALID' });
            }

            // /cart/add.js only returns the added lines; the lines-update event wants the whole cart
            if (linesUpdate) {
              fetch(`${routes.cart_url}.js`)
                .then((response) => response.json())
                .then((cart) => linesUpdate.resolve(cart))
                .catch((e) => linesUpdate.reject(e));
            }

            this.renderSections(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.id,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            if (e.code !== 'INVALID') console.error(e);
            this.dispatchCartErrorEvent(
              e.code === 'INVALID' ? e.message : window.cartStrings.error,
              e.code || 'SERVICE_UNAVAILABLE'
            );
            linesUpdate?.reject(e);
            this.setErrorMessage(e.code === 'INVALID' ? e.message : window.cartStrings.error);

            // Nothing was added, so the stepper goes back to the basket quantity
            const input = this.querySelector(`#Quantity-${id}`);
            if (input) {
              input.value = input.dataset.cartQuantity || '0';
              input.setAttribute('value', input.value);
              input.closest('quantity-input')?.validateQtyRules();
            }
          })
          .finally(() => {
            if (this.queue.length === 0) {
              this.toggleLoading(false);
              this.hideCartStatus();
            }
          });
      }

      cleanErrorMessageOnType(event) {
        const handleKeydown = () => {
          event.target.setCustomValidity(' ');
          event.target.reportValidity();
          event.target.removeEventListener('keydown', handleKeydown);
        };

        event.target.addEventListener('keydown', handleKeydown);
      }

      validateInput(target) {
        const targetValue = parseInt(target.value);
        const targetMin = parseInt(target.dataset.min);
        const targetStep = parseInt(target.step);

        if (target.max) {
          return (
            targetValue == 0 ||
            (targetValue >= targetMin && targetValue <= parseInt(target.max) && targetValue % targetStep == 0)
          );
        } else {
          return targetValue == 0 || (targetValue >= targetMin && targetValue % targetStep == 0);
        }
      }

      get quickOrderListTable() {
        return this.querySelector('.quick-order-list__table');
      }

      getSectionsToRender() {
        return [
          {
            id: this.id,
            section: this.dataset.section,
            selector: `#${this.id}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '#shopify-section-cart-icon-bubble',
          },
          {
            id: `quick-order-list-live-region-text-${this.dataset.productId}`,
            section: 'cart-live-region-text',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      toggleTableLoading(enable) {
        this.quickOrderListTable.classList.toggle('quick-order-list__container--disabled', enable);
        this.toggleLoading(enable);
      }

      async refresh(pageNumber = null) {
        const url = this.dataset.url || window.location.pathname;

        return fetch(`${url}?section_id=${this.dataset.section}&page=${pageNumber || this.currentPage}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const responseQuickOrderList = html.querySelector(`#${this.id}`);

            if (!responseQuickOrderList) {
              return;
            }

            this.allVariantsLoaded = false;
            this.innerHTML = responseQuickOrderList.innerHTML;
            this.initEventListeners();
          })
          .catch((e) => {
            console.error(e);
          });
      }

      renderSections(parsedState) {
        const { items, sections } = parsedState;

        this.getSectionsToRender().forEach(({ id, selector, section }) => {
          const sectionElement = document.getElementById(id);
          if (!sectionElement) return;

          const newSection = new DOMParser().parseFromString(sections[section], 'text/html').querySelector(selector);

          if (section === this.dataset.section) {
            if (this.queue.length > 0 || this.hasPendingQuantityUpdate) return;

            const focusedElement = document.activeElement;
            let focusTarget = focusedElement?.dataset?.target;
            if (focusTarget?.includes('remove')) {
              focusTarget = focusedElement.closest('quantity-popover')?.querySelector('[data-target*="increment-"]')
                ?.dataset.target;
            }

            const cartContents = this.querySelector('[data-cart-contents]');
            const newCartContents = newSection.querySelector('[data-cart-contents]');
            if (cartContents && newCartContents) {
              cartContents.innerHTML = newCartContents.innerHTML;
            }

            const table = this.quickOrderListTable;
            const newTable = newSection.querySelector('.quick-order-list__table');

            // only update variants if they are from the active page
            const shouldUpdateVariants =
              this.currentPage === (newSection.querySelector('.pagination-wrapper')?.dataset.page ?? '1');
            if (newTable && shouldUpdateVariants) {
              table.innerHTML = newTable.innerHTML;

              const newFocusTarget = this.querySelector(`[data-target='${focusTarget}']`);
              if (newFocusTarget) {
                newFocusTarget?.focus({ preventScroll: true });
              }

              this.initVariantEventListeners();
              // The server response only contains the current page, so any
              // merged multi-page rows need to be reloaded before filtering.
              this.allVariantsLoaded = false;
              this.applyVariantFilters();
            }
          } else if (section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', items.length === 0);
            sectionElement.querySelector(selector).innerHTML = newSection.innerHTML;
          } else {
            sectionElement.innerHTML = newSection.innerHTML;
          }
        });
      }

      scrollTop() {
        const { top } = this.getBoundingClientRect();

        if (this.isListInsideModal) {
          this.scrollIntoView();
        } else {
          window.scrollTo({ top: top + window.scrollY - (this.stickyHeader?.height || 0), behavior: 'instant' });
        }
      }

      scrollQuickOrderListTable(target) {
        const inputTopBorder = target.getBoundingClientRect().top;
        const inputBottomBorder = target.getBoundingClientRect().bottom;

        if (this.isListInsideModal) {
          const tableHeadCrossesInput =
            inputTopBorder < this.querySelector('.quick-order-list__table thead').getBoundingClientRect().bottom;

          if (tableHeadCrossesInput) {
            this.scrollToCenter(target);
          }
        } else {
          const stickyHeaderBottomBorder = this.stickyHeaderElement?.getBoundingClientRect().bottom;
          const inputOutsideOfViewPort =
            inputBottomBorder < this.querySelector('.variant-item__quantity-wrapper').offsetHeight;
          const stickyHeaderCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type !== 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder;
          const stickyHeaderScrollupCrossesInput =
            this.stickyHeaderElement &&
            this.stickyHeader.type === 'on-scroll-up' &&
            this.stickyHeader.height > inputTopBorder &&
            stickyHeaderBottomBorder > 0;

          if (inputOutsideOfViewPort || stickyHeaderCrossesInput || stickyHeaderScrollupCrossesInput) {
            this.scrollToCenter(target);
          }
        }
      }

      scrollToCenter(target) {
        target.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }

      handleScrollIntoView(event) {
        if ((event.key === 'Tab' || event.key === 'Enter') && this.allInputsArray.length !== 1) {
          this.scrollQuickOrderListTable(event.target);
        }
      }

      handleSwitchVariantOnEnter(event) {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;

        event.preventDefault();
        event.target.blur();

        if (!this.validateInput(event.target) || this.allInputsArray.length <= 1) return;

        const currentIndex = this.allInputsArray.indexOf(event.target);
        const offset = event.shiftKey ? -1 : 1;
        const nextIndex = (currentIndex + offset + this.allInputsArray.length) % this.allInputsArray.length;

        this.allInputsArray[nextIndex]?.select();
      }

      updateMultipleQty(items) {
        if (this.queue.length == 0) this.hasPendingQuantityUpdate = false;

        this.toggleLoading(true);
        this.showCartStatus(items);
        const url = this.dataset.url || window.location.pathname;
        const linesUpdate = this.startCartLinesUpdate(items);

        // Lines carrying order details (line item properties) are addressed by
        // line key, as Shopify recommends whenever a variant may appear on more
        // than one line; plain lines keep using the variant id as before.
        const updates = {};
        Object.entries(items).forEach(([variantId, quantity]) => {
          updates[this.lineKeyForVariant(variantId) || variantId] = quantity;
        });

        const body = JSON.stringify({
          updates,
          sections: this.getSectionsToRender().map(({ section }) => section),
          sections_url: `${url}?page=${this.currentPage}`,
        });

        this.updateMessage();
        this.setErrorMessage();

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              throw Object.assign(new Error(parsedState.errors), { code: 'INVALID' });
            }

            linesUpdate?.resolve(parsedState);
            this.renderSections(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.id,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            if (e.code !== 'INVALID') console.error(e);
            this.dispatchCartErrorEvent(
              e.code === 'INVALID' ? e.message : window.cartStrings.error,
              e.code || 'SERVICE_UNAVAILABLE'
            );
            linesUpdate?.reject(e);
            this.setErrorMessage(e.code === 'INVALID' ? e.message : window.cartStrings.error);
          })
          .finally(() => {
            if (this.queue.length === 0) {
              this.toggleLoading(false);
              this.hideCartStatus();
            }
            this.setRequestStarted(false);
          });
      }

      setErrorMessage(message = null) {
        this.errorMessageTemplate =
          this.errorMessageTemplate ??
          document.getElementById(`QuickOrderListErrorTemplate-${this.dataset.productId}`).cloneNode(true);
        const errorElements = document.querySelectorAll('.quick-order-list-error');

        errorElements.forEach((errorElement) => {
          errorElement.innerHTML = '';
          if (!message) return;
          const updatedMessageElement = this.errorMessageTemplate.cloneNode(true);
          updatedMessageElement.content.querySelector('.quick-order-list-error-message').innerText = message;
          errorElement.appendChild(updatedMessageElement.content);
        });
      }

      updateMessage(quantity = null) {
        const messages = this.querySelectorAll('.quick-order-list__message-text');
        const icons = this.querySelectorAll('.quick-order-list__message-icon');

        if (quantity === null || isNaN(quantity)) {
          messages.forEach((message) => (message.innerHTML = ''));
          icons.forEach((icon) => icon.classList.add('hidden'));
          return;
        }

        const isQuantityNegative = quantity < 0;
        const absQuantity = Math.abs(quantity);

        const textTemplate = isQuantityNegative
          ? absQuantity === 1
            ? window.quickOrderListStrings.itemRemoved
            : window.quickOrderListStrings.itemsRemoved
          : quantity === 1
          ? window.quickOrderListStrings.itemAdded
          : window.quickOrderListStrings.itemsAdded;

        messages.forEach((msg) => (msg.innerHTML = textTemplate.replace('[quantity]', absQuantity)));

        if (!isQuantityNegative) {
          icons.forEach((i) => i.classList.remove('hidden'));
        }
      }

      updateError(updatedValue, id) {
        let message = '';
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(id, message);
      }

      updateLiveRegions(id, message) {
        const variantItemErrorDesktop = document.getElementById(`Quick-order-list-item-error-desktop-${id}`);
        if (variantItemErrorDesktop) {
          variantItemErrorDesktop.querySelector('.variant-item__error-text').innerHTML = message;
          variantItemErrorDesktop.closest('tr').classList.remove('hidden');
        }
        if (variantItemErrorMobile)
          variantItemErrorMobile.querySelector('.variant-item__error-text').innerHTML = message;

        this.querySelector('#shopping-cart-variant-item-status').setAttribute('aria-hidden', true);

        const cartStatus = document.getElementById('quick-order-list-live-region-text');
        cartStatus.setAttribute('aria-hidden', false);

        setTimeout(() => {
          cartStatus.setAttribute('aria-hidden', true);
        }, 1000);
      }

      toggleLoading(loading, target = this) {
        target.querySelector('#shopping-cart-variant-item-status').toggleAttribute('aria-hidden', !loading);
      }
    }
  );
}
